import { ActionList, ActionMenu, Spinner, TextInput } from '@primer/react';
import { SearchIcon, SyncIcon } from '@primer/octicons-react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  PiuProductState,
  ProductMatchStatus,
} from '@upright/common/model/input/companies/products/product-input-ui/types';
import { useSearchUpgTaxonomy } from '@upright/ui/api/model/input/blanket/search-upg-taxonomy';
import useDebounce from '@upright/ui/common/generic/hooks/external/use-debounce';

interface ProductSelectorProps {
  product: PiuProductState;
  onProductSelect?: (productCode: string, productTitle: string) => void;
  showProductCode?: boolean;
}

export function ProductSelector({ product, onProductSelect, showProductCode = false }: ProductSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [selectedProduct, setSelectedProduct] = useState<{ code: string; title: string }>({
    code: product.productMatchResult?.productCode || '',
    title: product.productMatchResult?.bestChoice || '',
  });

  // Helper function to format product title with code prefix
  const formatProductTitle = (code: string, title: string): string => {
    if (showProductCode && code) {
      return `${code}: ${title}`;
    }
    return title;
  };

  // Search UPG taxonomy
  const { data: searchResults, isLoading: isSearching } = useSearchUpgTaxonomy(
    debouncedSearch,
    isOpen && debouncedSearch.length >= 2,
  );

  useEffect(() => {
    setSelectedProduct({
      code: product.productMatchResult?.productCode || '',
      title: product.productMatchResult?.bestChoice || '',
    });
  }, [product]);

  // Get top_k matches from productMatchResult
  const vectorMatches = product.productMatchResult?.top_k || [];
  const filteredTopK = vectorMatches
    .filter(([productCode]) => productCode.length !== 8) // Filter out mixins
    .slice(0, 6);

  const isManuallyOverridden = product.isManuallyOverridden || false;
  const canRevert = isManuallyOverridden && product.autoMatchedProductCode;

  const handleProductSelect = (productCode: string, productTitle: string) => {
    setSelectedProduct({ code: productCode, title: productTitle });
    onProductSelect?.(productCode, productTitle);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleRevert = () => {
    if (canRevert && product.autoMatchedProductCode) {
      const autoMatchedProductTitle = vectorMatches
        .filter(([code]) => code === product.autoMatchedProductCode)
        .map(([_code, title]) => title)[0];

      const productCode = product.autoMatchedProductCode;
      const productTitle = autoMatchedProductTitle || '';
      setSelectedProduct({ code: productCode, title: productTitle });
      onProductSelect?.(productCode, productTitle);
    }
  };

  return (
    <CoreProductChipWithWrap>
      <ActionMenu open={isOpen} onOpenChange={setIsOpen}>
        <ActionMenu.Anchor>
          <CoreProductChipWithWrap>
            <CoreProductChip as="span" $isManual={isManuallyOverridden}>
              {formatProductTitle(selectedProduct.code, selectedProduct.title)}
            </CoreProductChip>
            {product.productMatchStatus === ProductMatchStatus.RUNNING && <Spinner size="small" />}
          </CoreProductChipWithWrap>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="large">
          <SearchInputContainer>
            <TextInput
              leadingVisual={SearchIcon}
              placeholder="Search UPG taxonomy..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
              }}
              sx={{ width: '100%' }}
            />
          </SearchInputContainer>
          <ActionList>
            {canRevert && (
              <>
                <ActionList.Item onSelect={handleRevert} variant="default">
                  <ActionList.LeadingVisual>
                    <SyncIcon />
                  </ActionList.LeadingVisual>
                  Revert to auto-match {product.autoMatchedProductCode}
                </ActionList.Item>
                <ActionList.Divider />
              </>
            )}

            {filteredTopK.length > 0 && (
              <ActionList.Group title="Top matches" selectionVariant="single">
                {filteredTopK.map(([productCode, productTitle]) => (
                  <ActionList.Item
                    key={productCode}
                    onSelect={() => {
                      handleProductSelect(productCode, productTitle);
                    }}
                    selected={selectedProduct.code === productCode}
                  >
                    {formatProductTitle(productCode, productTitle)}
                  </ActionList.Item>
                ))}
              </ActionList.Group>
            )}

            {debouncedSearch.length >= 2 && (
              <>
                <ActionList.Divider />
                <ActionList.Group title="Search results" selectionVariant="single">
                  {isSearching && (
                    <ActionList.Item>
                      <Spinner size="small" /> Searching...
                    </ActionList.Item>
                  )}
                  {!isSearching && searchResults && searchResults.length === 0 && (
                    <ActionList.Item disabled>No results found</ActionList.Item>
                  )}
                  {!isSearching &&
                    searchResults &&
                    searchResults.slice(0, 10).map(result => {
                      // Skip if already in top_k
                      if (filteredTopK.some(([code]) => code === result.productCode)) {
                        return null;
                      }
                      return (
                        <ActionList.Item
                          key={result.productCode}
                          onSelect={() => {
                            handleProductSelect(result.productCode, result.productName);
                          }}
                          selected={selectedProduct.code === result.productCode}
                        >
                          {formatProductTitle(result.productCode, result.productName)}
                        </ActionList.Item>
                      );
                    })}
                </ActionList.Group>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {isManuallyOverridden && <ManualBadge>Manual</ManualBadge>}
      {product.productMatchStatus === ProductMatchStatus.RUNNING && <Spinner size="small" />}
    </CoreProductChipWithWrap>
  );
}

const CoreProductChipWithWrap = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
`;

const CoreProductChip = styled.div<{ $isManual: boolean }>`
  padding: var(--space__xs);
  border-radius: var(--border-radius__md);
  background-color: ${props => (props.$isManual ? 'var(--platform-orange-100)' : 'var(--color__blue-100)')};
  color: ${props => (props.$isManual ? 'var(--platform-orange-900)' : 'var(--platform-blue-900)')};
  font-weight: var(--font-weight__bold);
  font-size: var(--font-size__md);
  cursor: pointer;
  border: none;

  &:hover {
    background-color: ${props => (props.$isManual ? 'var(--platform-orange-200)' : 'var(--platform-blue-200)')};
  }
`;

const ManualBadge = styled.div`
  padding: 2px 6px;
  border-radius: var(--border-radius__xs);
  background-color: var(--platform-orange-500);
  color: white;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const SearchInputContainer = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-muted);
`;
